/* eslint-disable @typescript-eslint/no-unsafe-return, @typescript-eslint/no-explicit-any, complexity -- TODO: fix unsafe types and reduce complexity */
import { Box, Tooltip } from "@mui/material";
import { FieldsCard, FieldsData, Typography } from "@platform/design-system";
import { validateEmail, validateZipCode } from "@platform/fe-utils";
import {
  RfpBrokerageResponse,
  rfpBrokerageQueryKey,
  useRfpBrokerage,
} from "@platform/hooks";
import { BrokerPartnerUsersResponse } from "@platform/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { JSX, useMemo, useState } from "react";
import { useApi } from "@platform/api";
import { useSignedInUser } from "@platform/user/client";

import { STATE_ABBREVIATION } from "../../../constants";
import useToastNotificationService from "../../../hooks/useToastNotificationService";
import { extractErrorMessage } from "../../../utils";
import { buildFormattedAddress } from "../helpers";

type Props = { rfpId: string; refetchRpfDetails: VoidFunction };

const EMPTY_SUBMITTER_OPTIONS: SubmitterOption[] = [];

function RFPBrokerageCard({ rfpId, refetchRpfDetails }: Props): JSX.Element {
  const queryClient = useQueryClient();
  const { createToastNotification } = useToastNotificationService();
  const [isEdit, setIsEdit] = useState(false);
  const { isBrokerOrAgent } = useSignedInUser();
  const canEdit = !isBrokerOrAgent;

  const axiosInstance = useApi();
  const query = useRfpBrokerage({ rfpId });
  const brokerUsersQuery = useQuery<
    BrokerPartnerUsersResponse,
    Error,
    SubmitterOption[]
  >({
    queryKey: ["brokerPartnerUsers", query.data?.id],
    queryFn: () =>
      axiosInstance
        .post("brokerportal/v1/brokerPartnerUsers", {
          brokerId: query.data?.id,
        })
        .then((res) => res.data),
    select: mapBrokerPartnerUsersToSubmitterOptions,
    enabled: canEdit && query.data?.id != null,
  });

  const mutation = useMutation<
    void,
    Error,
    Record<string, string | number | null | Record<string, any>>
  >({
    mutationFn: (data) =>
      axiosInstance
        .patch(`brokerportal/v1/rfp/${rfpId}/brokerage`, data)
        .then((res) => res.data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: rfpBrokerageQueryKey(rfpId),
      });
      handleCancel();
      refetchRpfDetails();
    },
    onError: (error) => {
      createToastNotification({
        message: extractErrorMessage(error),
        severity: "error",
      });
    },
  });

  const handleCancel = (): void => {
    setIsEdit(false);
  };

  const handleEdit = async (): Promise<void> => {
    const result = await query.refetch();
    if (result.isError) {
      createToastNotification({
        message: extractErrorMessage(result.error),
        severity: "error",
      });
      return;
    }
    setIsEdit(true);
  };

  const status = mutation.isPending ? "saving" : isEdit ? "edit" : query.status;
  const submitterOptions = brokerUsersQuery.data ?? EMPTY_SUBMITTER_OPTIONS;

  const data = useMemo(
    () =>
      query.data
        ? mapResponseToFieldsCardData(query.data, submitterOptions)
        : undefined,
    [query.data, submitterOptions],
  );

  return (
    <FieldsCard
      title="Brokerage"
      status={status}
      error={query.error}
      gridContainerProps={{
        columns: 15,
      }}
      gridItemProps={{
        size: 3,
      }}
      skeleton={{
        count: 12,
        props: {
          height: 50,
        },
      }}
      data={data}
      onEdit={canEdit ? () => void handleEdit() : undefined}
      onCancel={canEdit ? handleCancel : undefined}
      onSave={
        canEdit
          ? (body) => {
              if (body) mutation.mutate(body);
              else handleCancel();
            }
          : undefined
      }
    />
  );
}

export function mapResponseToFieldsCardData(
  resp: RfpBrokerageResponse,
  submitterOptions: SubmitterOption[] = [],
): FieldsData[] {
  const address = resp.address;
  const addressForFormatting = address
    ? { ...address, addressLine1: address.address, zipCode: address.zipcode }
    : null;

  const arrAddress = buildFormattedAddress(addressForFormatting, {
    hasValidation: false,
  });
  const formattedAddress = arrAddress.filter(Boolean).join(", ");
  const submitterDisplayValue = formatSubmitterDisplayValue(resp.submitter);
  const selectedSubmitterOption = submitterOptions?.find(
    (option) => option.email === resp.submitter.email,
  );

  const data: FieldsData[] = [
    {
      label: "Broker",
      value: resp.name,
    },
    {
      label: "Broker Address",
      value: formattedAddress,
      editable: true,
      inputType: "group",
      inputName: "address",
      inputProps: {
        fields: [
          {
            value: address?.address ?? "",
            editable: true,
            inputType: "text",
            label: "Address",
            inputName: "address",
            gridItemProps: { size: 12 },
          },
          {
            value: address?.addressLine2 ?? "",
            editable: true,
            inputType: "text",
            label: "Address Line 2",
            inputName: "addressLine2",
            gridItemProps: { size: 12 },
          },
          {
            value: address?.city ?? "",
            editable: true,
            inputType: "text",
            label: "City",
            inputName: "city",
            gridItemProps: { size: 12 },
          },
          {
            value: address?.state ?? "",
            editable: true,
            inputType: "select",
            inputName: "state",
            label: "State",
            gridItemProps: { size: 4 },
            inputProps: {
              options: Object.entries(STATE_ABBREVIATION)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([stateName, abbreviation]) => ({
                  value: abbreviation,
                  label: `${stateName} - ${abbreviation}`,
                })),
            },
          },
          {
            value: address?.zipcode ?? "",
            editable: true,
            inputType: "text",
            label: "Zip Code",
            inputName: "zipcode",
            gridItemProps: { size: 8 },
            validators: [validateZipCode],
          },
        ],
        grid: {
          columns: 12,
          spacing: 1,
        },
      },
    },
    {
      label: "RFP Submitter",
      value: submitterDisplayValue,
      element: buildSubmitterFieldElement(resp.submitter),
      inputValue: selectedSubmitterOption?.value ?? "",
      editable: true,
      inputType: "select",
      inputName: "submitterId",
      inputProps: {
        options: submitterOptions,
        displayEmpty: true,
        renderValue: (selected) =>
          formatSelectedSubmitterOption(selected, submitterOptions),
        helperText:
          submitterOptions.length === 0
            ? "No broker users available"
            : undefined,
      },
    },
  ];

  if (resp.clientInfo.producer) {
    data.push({
      label: "Producer",
      value: {
        firstName: resp.clientInfo.producer.firstName,
        lastName: resp.clientInfo.producer.lastName,
        email: resp.clientInfo.producer.email,
      },
      editable: true,
      visibleViewMode: !!resp.clientInfo.producer,
      inputType: "group",
      inputName: "producer",
      inputProps: {
        fields: [
          {
            value: resp.clientInfo.producer?.firstName ?? "",
            editable: true,
            inputType: "text",
            label: "First Name",
            inputName: "firstName",
            gridItemProps: { size: 6 },
          },
          {
            value: resp.clientInfo.producer?.lastName ?? "",
            editable: true,
            inputType: "text",
            label: "Last Name",
            inputName: "lastName",
            gridItemProps: { size: 6 },
          },
          {
            value: resp.clientInfo.producer?.email ?? "",
            editable: true,
            inputType: "text",
            label: "Email",
            inputName: "email",
            validators: [validateEmail],
            gridItemProps: { size: 12 },
          },
        ],
        grid: {
          spacing: 1,
          columns: 12,
        },
      },
    });
  }

  const clientConfig = resp.clientInfo.config;

  if (!clientConfig) return data;

  if (clientConfig.clientExecutive) {
    data.push({
      label: "Client Executive",
      value: resp.clientInfo.clientExecutive,
      editable: false,
    });
  }
  if (clientConfig.practice) {
    data.push({
      label: "Practice",
      value: resp.clientInfo.practice?.name,
    });
  }
  if (clientConfig.corporateEntity) {
    data.push({
      label: "Corporate Entity",
      value: resp.clientInfo.corporateEntity?.name,
    });
  }
  if (clientConfig.officeLocation) {
    data.push({
      label: "Office Location",
      value: resp.clientInfo.officeLocation?.name,
    });
  }
  if (clientConfig.onedigitalOffice) {
    const onedigitalOfficeOptions = (
      resp.clientInfo.onedigitalOfficeOptions ?? []
    ).map((office) => ({ label: office, value: office }));

    data.push({
      label: "ONEDIGITAL Office",
      value: resp.clientInfo.onedigitalOffice ?? "",
      inputValue: resp.clientInfo.onedigitalOffice ?? "",
      editable: true,
      inputType: "select",
      inputName: "onedigitalOffice",
      inputProps: {
        options: onedigitalOfficeOptions,
        displayEmpty: true,
        helperText:
          onedigitalOfficeOptions.length === 0
            ? "No offices available"
            : undefined,
      },
    });
  }

  return data;
}

type SubmitterOption = {
  value: string;
  label: string;
  email: string;
  displayLabel?: JSX.Element;
};

type SubmitterUser = BrokerPartnerUsersResponse["members"][number] & {
  firstName?: string;
  lastName?: string;
  displayName?: string;
  name?: string;
};

function formatSubmitterLabel(fullName: string, email?: string | null): string {
  return fullName || email || "—";
}

function resolveSubmitterUserName(user: SubmitterUser): string {
  const fullName = [user.givenName, user.surname]
    .filter(Boolean)
    .join(" ")
    .trim();

  if (fullName) {
    return fullName;
  }

  const fallbackFullName = [user.firstName, user.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();

  return (
    fallbackFullName || user.displayName?.trim() || user.name?.trim() || "N/A"
  );
}

function formatSubmitterDisplayElement(
  fullName?: string | null,
  email?: string | null,
): JSX.Element {
  const normalizedName = fullName?.trim();
  const normalizedEmail = email?.trim();

  return (
    <Box sx={{ maxWidth: "100%", minWidth: 0 }}>
      <Box sx={{ lineHeight: 1.4 }}>{normalizedName || "—"}</Box>
      {normalizedName && normalizedEmail ? (
        <Tooltip title={normalizedEmail} placement="top-start">
          <Box
            sx={{
              lineHeight: 1.4,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: "100%",
            }}
          >
            {normalizedEmail}
          </Box>
        </Tooltip>
      ) : null}
    </Box>
  );
}

function formatSubmitterFieldElement(
  fullName?: string | null,
  email?: string | null,
): JSX.Element {
  return (
    <>
      <Typography
        variant="body-medium"
        data-testid="ds-field-label-rfp-submitter"
        display="flex"
        alignItems="center"
        columnGap={1.25}
      >
        <strong>RFP Submitter</strong>
      </Typography>
      {formatSubmitterDisplayElement(fullName, email)}
    </>
  );
}

function formatSubmitterDisplayValue(
  submitter: RfpBrokerageResponse["submitter"],
): string {
  const fullName = [submitter.firstName, submitter.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();

  return formatSubmitterLabel(fullName, submitter.email);
}

function buildSubmitterFieldElement(
  submitter: RfpBrokerageResponse["submitter"],
): JSX.Element {
  const fullName = [submitter.firstName, submitter.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();

  return formatSubmitterFieldElement(fullName, submitter.email);
}

export function mapBrokerPartnerUsersToSubmitterOptions(
  response: BrokerPartnerUsersResponse,
): SubmitterOption[] {
  const seen = new Set<string>();

  return [...response.members, ...response.owners]
    .filter((user) => user.accountEnabled)
    .filter((user) => {
      if (seen.has(user.id)) {
        return false;
      }

      seen.add(user.id);
      return true;
    })
    .map((user) => {
      const labelBase = resolveSubmitterUserName(user as SubmitterUser);

      return {
        value: user.id,
        email: user.mail,
        label: formatSubmitterLabel(labelBase, user.mail || "N/A"),
        displayLabel: formatSubmitterDisplayElement(labelBase, user.mail),
      };
    })
    .sort((left, right) => left.label.localeCompare(right.label));
}

function formatSelectedSubmitterOption(
  selected: string | number | readonly string[],
  options: SubmitterOption[],
): string | JSX.Element {
  if (typeof selected !== "string") {
    return "";
  }

  const option = options.find(
    (currentOption) => currentOption.value === selected,
  );

  return option?.displayLabel ?? option?.label ?? "";
}

export default RFPBrokerageCard;
